'use strict';

const _ = require('underscore');
const Qs = require('qs');
// Todo: Remove SV dependency
const SV = require('server-vars');
const BaseStore = require('fluxible/addons/BaseStore');
const Item = require('bunsen/models/inventory/Item-v3');
const Price = require('bunsen/models/inventory/Price.es');
const detailsConfig = require('../details/detailsConfig');
const detailsHelpers = require('../details/helpers/detailsHelpers');
const shard = require('bunsen/helpers/shard');
const tokenReplace = require('dibs-token-replace');
const get = require('lodash.get');
const detailsConstants = require('../constants/detailsConstants');
const enabler = require('../../global/utils/enabler');
const loginActions = require('../../global/actions/loginActions.es');
const currencyActionConstants = require('../../global/constants/currencyActionConstants.es');

/** @type {string[]} */
const KEY_FIELDS = [
    'pieces',
    'pricing',
    'actions',
    'returnPolicy',
    'lowestShipmentQuote', // graphql response, gets transformed to shipmentQuotes to populate model properly
    'status',
    'title',
    'activeHold',
];

class ItemStore extends BaseStore {
    constructor(dispatcher) {
        super(dispatcher);

        this._asyncItemData = {};

        this.item = new Item();

        // Some components manipulate the Item model directly and could cause data for the entire page to change.
        // Creating clone to avoid inadvertent manipulations to the item model from components
        // Todo: Do not pass Item to components, instead pass properties that they need via store getters
        this.itemClone = this.item.clone();

        /**
         * Item needs to be ajaxed in on the client to avoid displaying stale price information
         * @type {boolean}
         */
        this.isAsyncItemLoaded = false;

        /**
         * {object} detailsTitles
         * {object} detailsTitles.[vertical]
         * {string} detailsTitles.[vertical].[detail name] - title for that detail
         */
        this.detailsTitles = {};

        /**
         * {object} detailLinks                                              - the links available for the pdp's details
         * {object} detailLinks.[type of detail]                             - the type of detail (creator, condition, etc.).
         *                                                                     the object contains the links available for that type of detail.
         * {string} detailLinks.[type of detail].[display string for detail] - what will appear on the page for that type of
         *                                                                     detail. the string is the link for what will appear.
         * ie : {
         *          creator: {
         *              'Tomaso Buzzi': '/creators/tomaso-buzzi/furniture/'
         *          }
         *      }
         */
        this.detailLinks = {};

        /**
         * {string} detailTechniques - display data for the material notes detail
         */
        this.detailTechniques = '';

        /**
         * {object} creatorRoleReferences                             - reference set for creator roles
         * {object} creatorRoleReferences.[creator name]
         * {string} creatorRoleReferences.[creator name].key
         * {string} creatorRoleReferences.[creator name].type
         * {string} creatorRoleReferences.[creator name].displayName
         * {string} creatorRoleReferences.[creator name].enumName
         * {string} creatorRoleReferences.[creator name].nonUniqueName
         */
        this.creatorRoleReferences = {};

        /**
         * {object} periodReferences - reference set for jewelry periods
         * {object} periodReferences.[period name]
         * {string} periodReferences.[period name].key
         * {string} periodReferences.[period name].type
         * {string} periodReferences.[period name].displayName
         * {string} periodReferences.[period name].enumName
         * {string} periodReferences.[period name].nonUniqueName
         *
         * ie: {
         *         ART_DECO: {
         *                       displayName: 'Art Deco',
         *                       key: 'JPDDECO'
         *                   }
         *     }
         */
        this.periodReferences = {};

        /**
         * {object} conditionReferences                                - reference set for conditions
         * {object} conditionReferences.[condition name]
         * {string} conditionReferences.[condition name].key
         * {string} conditionReferences.[condition name].type
         * {string} conditionReferences.[condition name].displayName
         * {string} conditionReferences.[condition name].enumName
         * {string} conditionReferences.[condition name].nonUniqueName
         *
         * ie: {
         *         EXCELLENT: {
         *                        displayName: 'Excellent',
         *                        key: 'FCNDXLNT'
         *                    }
         *     }
         */
        this.conditionReferences = {};

        this.returnsCopy = {};
        this.breadcrumbs = {};
        this.goodRelationsMeta = {};
        this.pageData = {};
        this.copy = {};
        this.tradeReturnsEnabled = enabler.isEnabled('trade_returns_enabled');
        this._canUserRequestNetPrice = false;
        this._attributes = null;
    }

    _handleBootstrap({
        breadcrumbs,
        creatorRoleReferences,
        conditionReferences,
        copy,
        detailLinks,
        detailTechniques,
        shouldShowSuspended,
        detailsTitles,
        goodRelationsMeta,
        pageData,
        periodReferences,
        rawResult,
        returnsCopy,
        tradeReturnsEnabled,
        urlData,
    }) {
        // Async Item data takes precedence; default to cached Item data from `payload`
        const data = Object.assign({}, rawResult, this._asyncItemData);
        this.item = new Item(data);
        this.itemClone = this.item.clone();

        this.breadcrumbs = breadcrumbs;
        this.creatorRoleReferences = creatorRoleReferences;
        this.conditionReferences = conditionReferences;
        this.copy = copy;
        this.detailLinks = detailLinks;
        this.detailTechniques = detailTechniques;
        this.detailsTitles = detailsTitles;
        this.goodRelationsMeta = goodRelationsMeta;
        this.pageData = pageData;
        this.periodReferences = periodReferences;
        this.returnsCopy = returnsCopy;
        this.tradeReturnsEnabled = tradeReturnsEnabled;
        this.urlData = urlData;
        this.shouldShowSuspended = shouldShowSuspended;
        this._attributes = data.attributes;

        if (this.getIsPreview()) {
            this.isAsyncItemLoaded = true;
            this.emitChange();
        }
    }

    _handleAsyncLoadItem(item = {}) {
        const json = item.json;
        if (json) {
            let data = this.item.parse(json);

            // `pick` key properties to avoid polluting model with incomplete data
            data = _.pick(data, KEY_FIELDS);

            // graphql response provides a `lowestShipmentQuote` field
            if (data.lowestShipmentQuote) {
                data.shipmentQuotes = data.lowestShipmentQuote;
                delete data.lowestShipmentQuote;
            }

            this._asyncItemData = data;
            this.item.set(data);
            this.itemClone = this.item.clone();
        }

        // Always set to `true` regardless if json is defined to allow components to render with cached Item data
        this.isAsyncItemLoaded = true;
        this.emitChange();
    }

    _updateCurrency({ currency }) {
        if (currency && this._currency !== currency) {
            this._currency = currency;
            this.emit('change');
        }
    }

    _updateNetPriceStatus({ userCanRequestNetPrice }) {
        this._canUserRequestNetPrice = userCanRequestNetPrice;
    }

    getVpvParams(isTradeUser = false) {
        const shipmentQuotes = this.itemClone.get('shipmentQuotes');
        const hasPreQuoteShipping =
            shipmentQuotes && shipmentQuotes.getNonLocalShipmentQuotes().length;
        const vpvParams = {
            dd: this.itemClone.get('seller.id'),
            dn: this.itemClone.get('seller.sellerProfile.company'),
            id: this.itemClone.get('id'),
            status: this._getStatus(isTradeUser),
            pur: this.itemClone.canMakePurchase() ? 'y' : 'n',
            neg: this.isNegotiablePrice() ? 'y' : 'n',
            ship: hasPreQuoteShipping ? 'y' : 'n',
            ret: _.result(this.itemClone.get('returnPolicy'), 'accepts') ? 'y' : 'n',
        };
        return decodeURIComponent(Qs.stringify(vpvParams).toLowerCase());
    }
    _getStatus(isTradeUser = false) {
        const item = this.itemClone;
        const seller = item.get('seller');
        const isNetPrice = !!item.getPriceByType('NET');
        let status = 'Available-Price';
        if (seller.isStatusSuspended() || item.isDeletedStatus()) {
            status = 'Suspended';
        } else if (item.isOnHold()) {
            status = 'Hold';
        } else if (item.isSold()) {
            status = 'Sold';
        } else if (isNetPrice && isTradeUser) {
            status = 'Available-Net Price';
        } else if (item.hidingPrice()) {
            status = 'Available-No Price';
        }
        return `PDP-${status}`;
    }

    // Todo: Do not pass Item to components, instead pass properties that they need via store getters
    /** @deprecated */
    getItem() {
        return this.itemClone;
    }

    getPrice() {
        return this.getPriceByCurrency(Price.TYPE_RETAIL, this._currency);
    }

    getInitialPrice() {
        const originalPriceLock = this.item.getPriceLocksByType('ORIGINAL_PRICE');
        const originalPrice = originalPriceLock
            ? originalPriceLock.get('price.amount.convertedAmounts')
            : null;

        if (originalPrice) {
            return originalPrice.get(this._currency);
        }

        return null;
    }

    /**
     * @returns {number}
     */
    getCategoryOrdinalNumber() {
        const urls = this.item.getPdpUrls() || [];
        const urlData = this.getUrlData();
        let nth = 1;
        let i = 0;

        while (i < urls.length) {
            if (urls[i] === urlData.path) {
                nth = i + 1;
                break;
            }
            i++;
        }
        return nth;
    }

    getItemCategories() {
        return this.item.getItemCategories();
    }

    getItemCategoryGroup(nth = this.getCategoryOrdinalNumber()) {
        return this.getItemCategories().findCategoryByPrecedence(nth);
    }

    /** * @deprecated */
    getCategories() {
        return this.item.getCategories();
    }

    getHidePrice() {
        return this.item.isPriceHidden();
    }

    getCanMakePurchase() {
        return this.item.canMakePurchase();
    }

    getCreatorData() {
        const creator = this.getFromSV('item.creators.crea.creator_name.0');
        const result = { name: '', uri: '' };

        if (creator) {
            result.name = creator.name;
            result.link = creator.link;
        }

        return result;
    }

    getCreator() {
        return this.item.getCreator();
    }

    getPriceByCurrency(type, currency) {
        return this.item.getPriceIn(type, currency);
    }

    getIsArt() {
        return this.item.isArt();
    }

    getDetailsConfig() {
        const vertical = this.getFromItemModel('vertical');
        return detailsConfig[vertical];
    }

    isNegotiablePrice() {
        return this.item.isNegotiablePriceByType(Price.TYPE_RETAIL);
    }

    isTradeReturnsEnabled() {
        return this.tradeReturnsEnabled;
    }

    /**
     * Grabs a set of helpers, based on the item's vertical, and uses those
     * helpers to calculate the pdp's details. Copy for the details titles
     * is not determined in these helpers. This method returns only the
     * information that will appear under a detail's title.
     * @returns {Object} - keys are the type of detail, ie: 'creator'
     *                     values are arrays that contain other arrays or objects in the format needed by the
     *                     component that will render them. see documentation for a specific
     *                     detail helper to find the format for that detail.
     */
    getDetails() {
        const vertical = this.getFromItemModel('vertical');
        const helpers = detailsHelpers.getHelpers(vertical);
        const details = {};

        _.each(helpers, (helper, key) => {
            const data = helper({
                item: this.item,
                links: this.detailLinks,
                creatorRoleReferences: this.creatorRoleReferences,
                periodReferences: this.periodReferences,
                conditionReferences: this.conditionReferences,
                currency: this._currency,
                detailTechniques: this.detailTechniques,
                copy: this.copy,
            });
            if (!_.isEmpty(data)) {
                details[key] = data;
            }
        });
        return details;
    }

    getDetailsTitles() {
        return this.detailsTitles;
    }

    getDealerData() {
        if (!this.dealer) {
            const dealer = _.clone(this.getFromSV('item.dealer'));
            // FINDING-6978 dealer logo does NOT return the cdn domain with the image
            dealer.logo = shard.getUrl(dealer.logo, this.getFromSV('settings.imagesHosts'));
            dealer.sinceYear = dealer.since_year;
            dealer.urlLabel = dealer.url_label;
            // Determine if this is a "dealer" or a "gallery".
            dealer.type = this.getFromSV('copy.dealer_info.title').toLowerCase();

            // clean up response data so consistent properties are used
            dealer.url_label = undefined; // eslint-disable-line camelcase
            dealer.since_year = undefined; // eslint-disable-line camelcase

            dealer.dealerReferenceTitle = get(
                this.detailsTitles,
                detailsConstants.title.dealerReference
            );
            dealer.dealerReferenceNumber = this.item.get('dealerReference');
            this.dealer = dealer;
        }
        return this.dealer;
    }

    getUrl() {
        return document.URL;
    }

    getUrlData() {
        return this.urlData;
    }

    getSoldIndividually(model = {}) {
        if (typeof model.isSoldIndividually === 'function') {
            return model.isSoldIndividually();
        }
        return this.item.soldIndividually();
    }

    getIsOnHold() {
        return this.item.isOnHold();
    }

    getIsSold() {
        // both of these conditions must stay combined together:
        // 1. (itemModel.isSold && SV.data.isSold)
        // 2. (itemModel.get('status.isCompleteStatus') === 'N' && shouldShowSuspended)
        // If #2 uses an OR operator, then shit breaks and doesn't show the price
        // b/c for whatever reason, it seems like a lot of itemModel status.isCompleteStatus === 'N'
        // even if they are on a category search page
        // Sold (using sold template) or suspended flow
        return (
            (this.item.isSold() && this.getFromSV('data.isSold')) ||
            (!this.item.isCompleteStatus() && this.getShouldShowSuspended())
        );
    }

    getIsUnlisted() {
        return this.getFromSV('data.isUnlisted');
    }

    getShouldShowSuspended() {
        return this.shouldShowSuspended;
    }

    getSocialData() {
        if (!this.socialData) {
            // FINDING-6978 no longer need to use shard for images
            this.socialData = _.clone(this.getFromSV('pdpSocialData'));
        }

        return this.socialData;
    }

    getReturnsCopyFormatted(vertical) {
        const copy = Object.assign({}, this.returnsCopy);
        const verticalOverrides = copy.verticalOverrides;

        if (vertical && verticalOverrides && verticalOverrides[vertical]) {
            Object.assign(copy, verticalOverrides[vertical]);
        }

        return _.object(
            _.map(copy, function(text, key) {
                if (typeof text !== 'string') {
                    return text;
                }
                return [key, tokenReplace(text, copy, '%')];
            })
        );
    }

    getSupportCopy() {
        return this.copy.support;
    }

    getReturnMessage() {
        const returnPolicy = this.getFromItemModel('returnPolicy');
        const vertical = this.getFromItemModel('vertical');
        const isTradeReturnAvailable = this.isTradeReturnAvailable();
        let returnMessage;

        if (!(returnPolicy && vertical)) {
            return returnMessage;
        }

        const acceptsReturns = returnPolicy.acceptsReturns();
        const notifyDays = returnPolicy.get('notifyDays');
        const copyFormatted = this.getReturnsCopyFormatted(vertical);

        if (!acceptsReturns) {
            if (isTradeReturnAvailable) {
                return get(this, `returnsCopy.tradeReturns.pdp.cannotBeReturned`);
            }
            return this.returnsCopy.cannotBeReturned;
        }

        if (acceptsReturns && notifyDays) {
            returnMessage =
                notifyDays === 1
                    ? copyFormatted.returnsAcceptedSingular
                    : copyFormatted.returnsAcceptedPlural;
            returnMessage = returnMessage.replace('%s', notifyDays);
        }

        if (isTradeReturnAvailable) {
            returnMessage = `${returnMessage} ${get(
                this,
                `returnsCopy.tradeReturns.pdp.returnsAccepted`
            )}`;
        }

        return returnMessage;
    }

    isTradeReturnAvailable() {
        const enabled = this.isTradeReturnsEnabled();
        const tradeBuyer = this._canUserRequestNetPrice;
        return enabled && tradeBuyer && this.item.isTradeReturnAvailable();
    }

    getBreadcrumbs() {
        return this.breadcrumbs;
    }

    getGoodRelationsMeta() {
        return this.goodRelationsMeta;
    }

    getCopy() {
        return this.copy;
    }

    getPageData() {
        return this.pageData || {};
    }

    getFromSV(key) {
        return SV.get(key);
    }

    getFromItemModel(key) {
        return this.item.get(key);
    }

    getIsAsyncItemLoaded() {
        return this.isAsyncItemLoaded;
    }

    getIsPreview() {
        return this.getPageData().isPreview;
    }

    getAttribute(attribute) {
        return get(this._attributes, attribute) || null;
    }

    getCustomizationNote() {
        return this.getAttribute('customizable[0].text') || '';
    }
}

ItemStore.storeName = 'ItemStore';

ItemStore.handlers = {
    BOOTSTRAP_ITEM_STORE: '_handleBootstrap',
    ASYNC_LOAD_ITEM: '_handleAsyncLoadItem',
    [currencyActionConstants.CURRENCY_CHANGED]: '_updateCurrency',
    [loginActions.ACTION_NET_PRICE]: '_updateNetPriceStatus',
};

module.exports = ItemStore;



// WEBPACK FOOTER //
// ./assets/js/newPdp/stores/ItemStore.es.js